import { Button } from '@thanx/uikit/button'
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import { Link } from 'react-router-dom'
import { StyleObject, useStyletron } from 'styletron-react'
import { useIsMobile } from 'utilities/responsive'

const t = buildTranslate('page_header')

// The copy column takes `mr-auto` and is free to wrap; the action column is not.
// Without this the description squeezes it and a two-word CTA breaks across lines
// (e.g. the Order 👍/👎 report's "Notification settings"). Exported because
// styletron's css() is a no-op in tests, so the rule never reaches the DOM.
export const headerActionsStyle: StyleObject = { flexShrink: 0 }

// The `xl` Bootstrap spacer ($spacer * 3, see stylesheets/variables/bs4/_spacing.scss),
// which is what the title's `mr-xl` below already reserves.
const HEADER_GUTTER = '3rem'

// `headerActionsStyle` keeps the action column from yielding, but nothing kept the
// description from running all the way up to its edge — at some widths the last word
// visually touched the CTA. A gap on the row is the one declaration that can win here:
// the copy column already carries `mr-auto`, so a second `margin-right` class would
// resolve by CSS source order, and padding on the copy column would inset a
// React-node `description` too. Exported because styletron's css() is a no-op in
// tests, so the rule never reaches the DOM.
export const headerGutterStyle: StyleObject = { columnGap: HEADER_GUTTER }

export type Props = {
  children?: React.ReactNode
  // ReactNode (not just string) so a page can render a rich H1 — e.g. the
  // Orders 👍/👎 report's thumb glyphs (W4). A widening: every existing string
  // caller still type-checks, and the only consumer is <Text.Header2>{title}</…>,
  // whose children already accept a node.
  title: React.ReactNode
  description?: string | React.ReactNode
  backPath?: string
  buttonAction?: () => void
  buttonText?: string
  buttonIsDisabled?: boolean
  menu?: React.ReactNode
  topComponent?: React.ReactNode
  learnMoreUrl?: string
  containerClassName?: string
  // Exposes the description row's own DOM node so a consumer can
  // `scrollIntoView()` it — e.g. OrderFeedback scrolls the title out of
  // view (and past this page's own scrollable nav chrome) when the
  // Feedback tab activates, so the inbox gets the extra vertical room
  // (confirmed against the Sketch prototype, v82: selecting Feedback
  // scrolls the page so only the description remains above the tab bar).
  // `Text` (uikit) doesn't forward refs, so this wraps the description in
  // a plain ref'd div rather than trying to ref `Text.BodyText3` itself.
  // Also given `tabIndex={-1}` (below) so a consumer can move keyboard
  // focus here alongside the scroll — `scrollIntoView` alone moves the
  // viewport but leaves focus wherever it was, which strands
  // keyboard/switch-control users off-screen.
  // Optional and unused by every other consumer — zero behavior change.
  descriptionRef?: React.RefObject<HTMLDivElement>
}

const PageHeader: React.FC<Props> = props => {
  const {
    children,
    title,
    description,
    backPath,
    buttonAction,
    buttonText,
    buttonIsDisabled = false,
    menu,
    topComponent,
    learnMoreUrl,
    containerClassName = '',
    descriptionRef,
  } = props
  const isMobile = useIsMobile()
  const [css] = useStyletron()
  const hasAction = (!!buttonAction && !!buttonText) || !!menu

  return (
    <Container
      fluid
      className={`w-100 h-100 pt-m px-0 white-bkg border-bottom-1 grey-20-border overflow-auto${
        containerClassName ? ` ${containerClassName}` : ''
      }`}
    >
      <Switch condition={!!backPath}>
        <Link
          className="p-s grey-90 grey-10-hover-bkg grey-90-hover bold border-radius-5 no-underline mb-l"
          to={backPath || ''}
        >
          <i className="mr-s fa fa-arrow-left grey-90" title={t('back')} />
          {t('back')}
        </Link>
      </Switch>
      <Container
        className={['py-m d-flex px-0', hasAction ? css(headerGutterStyle) : '']
          .filter(Boolean)
          .join(' ')}
      >
        <div className="d-flex flex-column mr-auto">
          <Switch condition={!!topComponent}>
            <div className="mb-xs">{topComponent}</div>
          </Switch>
          <Text.Header2 className="m-0 mr-xl">{title}</Text.Header2>
          <div ref={descriptionRef} tabIndex={descriptionRef ? -1 : undefined}>
            <Switch
              condition={typeof description === 'string'}
              fallback={description}
            >
              <Text.BodyText3 tag="p" className="mt-xs mb-m">
                {description}
                {!!learnMoreUrl && (
                  <>
                    {' '}
                    <a
                      href={learnMoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('learn_more')}
                    </a>
                  </>
                )}
              </Text.BodyText3>
            </Switch>
          </div>
        </div>
        <div
          className={[
            'd-flex align-items-center',
            css(headerActionsStyle),
            isMobile && hasAction ? 'my-m' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Switch condition={!!buttonAction && !!buttonText}>
            <Button
              className={menu ? 'mr-m' : ''}
              onClick={buttonAction}
              disabled={buttonIsDisabled}
            >
              {buttonText}
            </Button>
          </Switch>
          <Switch condition={!!menu}>{menu}</Switch>
        </div>
      </Container>
      {children}
    </Container>
  )
}

export default PageHeader
